// One shared entitlements lookup per signed-in user, so guarded routes and the cards don't each
// refetch (and show a spinner) on every mount. Failures are not cached: they resolve to null
// (fail-open) and the next mount tries again.
import { auth } from '../config/firebase';
import { productsApi } from './productsApi';
import type { ProductEntitlement } from './productsApi';

export type Entitlements = Record<string, ProductEntitlement> | null;

interface Entry {
  uid: string | null;
  promise: Promise<Entitlements>;
  value?: Entitlements; // set once resolved successfully
  resolved: boolean;
}

let entry: Entry | null = null;

const currentUid = (): string | null => auth.currentUser?.uid ?? null;

/** Drop the cached answer (sign-out, or a different user signing in). */
export function clearEntitlementsCache(): void {
  entry = null;
}

/** The cached answer for the current user, or undefined when there isn't one yet. */
export function peekEntitlements(): Entitlements | undefined {
  if (entry && entry.resolved && entry.uid === currentUid()) return entry.value;
  return undefined;
}

/** Resolves to the plan's products, or null when it could not be loaded (fail-open). */
export function loadEntitlements(): Promise<Entitlements> {
  const uid = currentUid();
  if (entry && entry.uid === uid) return entry.promise;

  const mine: Entry = { uid, resolved: false, promise: Promise.resolve(null) };
  mine.promise = productsApi
    .getProducts()
    .then((res) => {
      mine.value = res.products ?? {};
      mine.resolved = true;
      return mine.value;
    })
    .catch((err) => {
      console.error('Failed to load product entitlements:', err);
      if (entry === mine) entry = null;
      return null;
    });
  entry = mine;
  return mine.promise;
}
